import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import UCPageShell from "../../../components/ui/UCPageShell";
import SectionTitle from "../../../components/shared/SectionTitle";
import UCTable from "../../../components/ui/UCTable";
import StressDot from "../../../components/ui/StressDot";
import AceAvatar from "../../../components/shared/AceAvatar";
import { fetchUsersClassesOverview } from "../../../services/usersClassesService";
import {
  getAssignedClasses,
  getCurrentUser,
  isAdminUser,
} from "../../../utils/auth";

function normalizeClass(value) {
  return String(value || "").trim().toLowerCase();
}

function getBackTarget(state) {
  return (
    state?.backTo ||
    (state?.fromStats ? "/stats" : "/users-classes")
  );
}

function getBackLabel(state) {
  return (
    state?.backLabel ||
    (state?.fromStats ? "← Zurück zu Statistiken" : "← Übersicht")
  );
}

function getLastActiveScore(value) {
  const normalized = String(value || "").toLowerCase();

  if (normalized.includes("heute")) return 0;
  if (normalized.includes("gestern")) return 1;
  if (normalized.includes("vor 2 tagen")) return 2;
  return 3;
}

function scopeOverview(data) {
  const currentUser = getCurrentUser();

  if (isAdminUser(currentUser)) {
    return data || { users: [], classes: [] };
  }

  const allowed = new Set(
    (getAssignedClasses(currentUser) || []).map(normalizeClass)
  );

  const users = (data?.users || []).filter((user) =>
    allowed.has(normalizeClass(user?.className))
  );

  const classes = (data?.classes || []).filter((klass) =>
    allowed.has(normalizeClass(klass?.name || klass?.className || klass?.id))
  );

  return { users, classes };
}

function StreakPill({ value }) {
  const active = value > 0;

  return (
    <span
      className={`ac-uc-pill ${
        active ? "ac-uc-pill-streak" : "ac-uc-pill-streak-off"
      }`}
    >
      {active ? `${value} Tage` : "Keine aktive Streak"}
    </span>
  );
}

export default function UsersListPage() {
  const navigate = useNavigate();
  const location = useLocation();

  const [q, setQ] = useState("");
  const [className, setClassName] = useState("all");
  const [stress, setStress] = useState("all");
  const [sortBy, setSortBy] = useState("name");
  const [overview, setOverview] = useState({ users: [], classes: [] });
  const [loading, setLoading] = useState(true);

  const backTo = useMemo(() => getBackTarget(location.state), [location.state]);
  const backLabel = useMemo(() => getBackLabel(location.state), [location.state]);

  useEffect(() => {
    let mounted = true;

    async function load() {
      try {
        const data = await fetchUsersClassesOverview();
        if (!mounted) return;
        setOverview(scopeOverview(data));
      } catch (error) {
        console.error("Users konnten nicht geladen werden:", error);
        if (!mounted) return;
        setOverview({ users: [], classes: [] });
      } finally {
        if (mounted) setLoading(false);
      }
    }

    load();

    return () => {
      mounted = false;
    };
  }, []);

  const classOptions = useMemo(() => {
    return overview.classes
      .map((item) => item.name)
      .sort((a, b) => a.localeCompare(b, "de"));
  }, [overview.classes]);

  const filtered = useMemo(() => {
    const query = q.trim().toLowerCase();

    return overview.users
      .filter((user) => {
        if (className === "all") return true;
        return String(user.className) === String(className);
      })
      .filter((user) => {
        const avgStress = Number(user.averageStress || 0);

        if (stress === "all") return true;
        if (stress === "motiviert") return avgStress <= 2;
        if (stress === "ruhig") return avgStress === 3;
        return avgStress >= 4;
      })
      .filter((user) => {
        if (!query) return true;

        const searchText = `${user.displayName} ${user.email} ${user.className}`.toLowerCase();
        return searchText.includes(query);
      })
      .sort((a, b) => {
        if (sortBy === "streak") {
          return Number(b.streak || 0) - Number(a.streak || 0);
        }

        if (sortBy === "stress") {
          return Number(b.averageStress || 0) - Number(a.averageStress || 0);
        }

        if (sortBy === "active") {
          return getLastActiveScore(a.lastActiveLabel) - getLastActiveScore(b.lastActiveLabel);
        }

        return String(a.displayName).localeCompare(String(b.displayName), "de");
      });
  }, [overview.users, q, className, stress, sortBy]);

  const quickStats = useMemo(() => {
    return {
      activeStreaks: filtered.filter((user) => Number(user.streak || 0) > 0).length,
      elevated: filtered.filter((user) => Number(user.averageStress || 0) >= 4).length,
      activeToday: filtered.filter((user) =>
        String(user.lastActiveLabel).toLowerCase().includes("heute")
      ).length,
    };
  }, [filtered]);

  const cols = useMemo(
    () => [
      {
        key: "name",
        label: "Name",
        width: "280px",
        headerStyle: { minWidth: "280px" },
        cellStyle: { minWidth: "280px" },
        render: (user) => (
          <div className="ac-uc-personcell">
            <div className="ac-uc-personname">{user.displayName}</div>
            <div className="ac-uc-personmeta">
              {user.email || "Keine E-Mail gespeichert"}
            </div>
          </div>
        ),
      },
      {
        key: "className",
        label: "Klasse",
        width: "120px",
      },
      {
        key: "stress",
        label: "Stressampel",
        width: "140px",
        render: (user) => <StressDot stress={user.averageStress} />,
      },
      {
        key: "streak",
        label: "Streak",
        width: "170px",
        render: (user) => <StreakPill value={Number(user.streak || 0)} />,
      },
      {
        key: "lastActiveLabel",
        label: "Zuletzt aktiv",
        width: "150px",
      },
      {
        key: "action",
        label: "Aktion",
        width: "120px",
        render: (user) => (
          <button
            type="button"
            onClick={() =>
              navigate(`/users-classes/users/${user.id}`, {
                state: {
                  ...location.state,
                  backTo,
                  backLabel,
                },
              })
            }
            className="ac-ghostbtn ac-uc-tablebtn"
          >
            Details
          </button>
        ),
      },
    ],
    [navigate, location.state, backTo, backLabel]
  );

  return (
    <UCPageShell
      title="Nutzer:innen"
      right={
        <button
          type="button"
          onClick={() => navigate(backTo)}
          className="ac-ghostbtn"
        >
          {backLabel}
        </button>
      }
    >
      <div className="space-y-5">
        <section className="ac-soft-panel ac-uc-hero">
          <div className="ac-uc-hero-grid">
            <div>
              <h2 className="ac-uc-hero-title">Nutzer:innen</h2>
              <p className="ac-uc-hero-text">
                Hier siehst du alle registrierten Nutzer:innen. Über „Details“
                gelangst du direkt zum Profil, zu Aktivität, Belastung und
                Aufgaben.
              </p>

              <div className="ac-uc-chip-row">
                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">
                    {loading ? "…" : filtered.length}
                  </span>
                  <span className="ac-uc-chip-label">Treffer</span>
                </div>

                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">
                    {loading ? "…" : quickStats.activeToday}
                  </span>
                  <span className="ac-uc-chip-label">heute aktiv</span>
                </div>

                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">
                    {loading ? "…" : quickStats.activeStreaks}
                  </span>
                  <span className="ac-uc-chip-label">aktive Streaks</span>
                </div>

                <div className="ac-uc-chip">
                  <span className="ac-uc-chip-value">
                    {loading ? "…" : quickStats.elevated}
                  </span>
                  <span className="ac-uc-chip-label">kritische Profile</span>
                </div>
              </div>

              <div className="ac-uc-filtergrid ac-uc-filtergrid-users">
                <input
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Suche: Name, E-Mail oder Klasse…"
                  className="ac-uc-input"
                />

                <select
                  value={className}
                  onChange={(e) => setClassName(e.target.value)}
                  className="ac-select w-full"
                >
                  <option value="all">Alle Klassen</option>
                  {classOptions.map((option) => (
                    <option key={option} value={option}>
                      {option}
                    </option>
                  ))}
                </select>

                <select
                  value={stress}
                  onChange={(e) => setStress(e.target.value)}
                  className="ac-select w-full"
                >
                  <option value="all">Alle Status</option>
                  <option value="motiviert">Motiviert</option>
                  <option value="ruhig">Ruhig</option>
                  <option value="belastet">Belastet</option>
                </select>

                <select
                  value={sortBy}
                  onChange={(e) => setSortBy(e.target.value)}
                  className="ac-select w-full"
                >
                  <option value="name">Name A–Z</option>
                  <option value="streak">Streak absteigend</option>
                  <option value="stress">Stress zuerst</option>
                  <option value="active">Zuletzt aktiv</option>
                </select>
              </div>
            </div>

            <div className="ac-uc-hero-side">
              <div className="ac-uc-acebox">
                <AceAvatar variant="motiviert1" size={56} />
                <div className="ac-uc-acebubble">
                  Ace sagt: Über Filter, Belastung und Aktivität findest du
                  auffällige Muster deutlich schneller.
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="ac-soft-panel ac-uc-block">
          <SectionTitle
            title="Nutzer:innen-Liste"
            subtitle={`Treffer: ${filtered.length}`}
          />

          <div className="mt-4">
            {loading ? (
              <div className="ac-uc-empty">Lade Nutzer:innen…</div>
            ) : filtered.length === 0 ? (
              <div className="ac-uc-empty">
                Kein Treffer. Versuche einen anderen Suchbegriff oder ändere den
                Filter.
              </div>
            ) : (
              <UCTable columns={cols} rows={filtered} rowKey={(user) => user.id} />
            )}
          </div>
        </section>
      </div>
    </UCPageShell>
  );
}